import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import Logo from "@/components/Logo";
import {
  SECTION_IDS,
  stripLang,
  useLang,
  useSectionNav,
} from "@/lib/navigation";

const Header = () => {
  const { t } = useTranslation();
  const lang = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const goToSection = useSectionNav();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const sectionLinks = [
    { id: SECTION_IDS.why, label: t("nav.why"), testId: "nav-link-why" },
    { id: SECTION_IDS.circuit, label: t("nav.circuit"), testId: "nav-link-circuit" },
    {
      id: SECTION_IDS.transparency,
      label: t("nav.transparency"),
      testId: "nav-link-transparency",
    },
    { id: SECTION_IDS.imagery, label: t("nav.imagery"), testId: "nav-link-imagery" },
  ];

  const legalLinks = [
    { to: `/${lang}/privacy-policy`, label: t("nav.privacy"), testId: "nav-link-privacy" },
    { to: `/${lang}/cookie-policy`, label: t("nav.cookies"), testId: "nav-link-cookies" },
    { to: `/${lang}/terms-conditions`, label: t("nav.terms"), testId: "nav-link-terms" },
  ];

  const switchLanguage = (target) => {
    if (target === lang) return;
    const rest = stripLang(location.pathname);
    const nextPath = rest === "/" ? `/${target}` : `/${target}${rest}`;
    navigate(`${nextPath}${location.hash || ""}`);
    setOpen(false);
  };

  const handleSection = (id) => {
    setOpen(false);
    goToSection(id);
  };

  const LanguageSwitcher = ({ idSuffix = "" }) => (
    <div
      className="flex items-center rounded-lg border border-border bg-card p-0.5"
      role="group"
      aria-label={t("nav.language_label")}
      data-testid={`header-language-switcher${idSuffix}`}
    >
      {[
        { code: "ar", short: t("nav.lang_es"), full: t("nav.lang_es_full") },
        { code: "en", short: t("nav.lang_en"), full: t("nav.lang_en_full") },
      ].map((item) => (
        <button
          key={item.code}
          type="button"
          onClick={() => switchLanguage(item.code)}
          aria-pressed={lang === item.code}
          aria-label={item.full}
          title={item.full}
          className={`font-data rounded-md px-2.5 py-1 text-[11px] font-medium tracking-wider transition-colors ${
            lang === item.code
              ? "bg-primary text-primary-foreground"
              : "text-[hsl(var(--ink-2))] hover:bg-secondary"
          }`}
          data-testid={`header-language-${item.code}${idSuffix}`}
        >
          {item.short}
        </button>
      ))}
    </div>
  );

  return (
    <header
      className={`site-header sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/75 ${
        scrolled ? "shadow-[0_1px_16px_hsl(214_30%_10%/0.07)]" : ""
      }`}
      data-testid="site-header"
    >
      <div className="container-page flex h-[72px] items-center justify-between gap-4">
        <Link
          to={`/${lang}`}
          className="flex items-center"
          aria-label={t("brand.name")}
          data-testid="header-logo-link"
        >
          <Logo title={t("brand.tagline")} />
        </Link>

        <nav
          className="hidden items-center gap-7 lg:flex"
          aria-label={t("nav.menu_title")}
          data-testid="header-nav"
        >
          <Link
            to={`/${lang}`}
            className="link-underline text-[13px] font-medium text-[hsl(var(--ink-2))] transition-colors hover:text-foreground"
            data-active={stripLang(location.pathname) === "/" ? "true" : "false"}
            data-testid="nav-link-home"
          >
            {t("nav.home")}
          </Link>
          {sectionLinks.map((link) => (
            <button
              key={link.id}
              type="button"
              onClick={() => handleSection(link.id)}
              className="link-underline text-[13px] font-medium text-[hsl(var(--ink-2))] transition-colors hover:text-foreground"
              data-testid={link.testId}
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>
          <Button
            type="button"
            onClick={() => handleSection(SECTION_IDS.contact)}
            className="hidden h-9 rounded-lg px-4 text-[13px] md:inline-flex"
            data-testid="header-cta-button"
          >
            {t("nav.cta")}
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={t("nav.open_menu")}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-card text-foreground transition-colors hover:bg-secondary lg:hidden"
            data-testid="header-mobile-menu-button"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="site-header w-[88vw] max-w-sm overflow-y-auto bg-background p-0"
          data-testid="header-mobile-menu"
        >
          <SheetHeader className="flex flex-row items-center justify-between border-b border-border px-5 py-4">
            <SheetTitle className="font-display text-base font-semibold">
              {t("nav.menu_title")}
            </SheetTitle>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("nav.close_menu")}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card transition-colors hover:bg-secondary"
              data-testid="header-mobile-menu-close"
            >
              <X className="h-4 w-4" />
            </button>
          </SheetHeader>

          <div className="flex flex-col gap-1 px-5 py-5">
            <Link
              to={`/${lang}`}
              onClick={() => setOpen(false)}
              className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
              data-testid="mobile-nav-link-home"
            >
              {t("nav.home")}
            </Link>
            {sectionLinks.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => handleSection(link.id)}
                className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-secondary"
                data-testid={`mobile-${link.testId}`}
              >
                {link.label}
              </button>
            ))}
            <Separator className="my-3" />
            {legalLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm text-[hsl(var(--ink-2))] transition-colors hover:bg-secondary"
                data-testid={`mobile-${link.testId}`}
              >
                {link.label}
              </Link>
            ))}
            <Separator className="my-3" />
            <div className="px-1 pb-2">
              <LanguageSwitcher idSuffix="-mobile" />
            </div>
            <Button
              type="button"
              onClick={() => handleSection(SECTION_IDS.contact)}
              className="mt-2 h-11 w-full rounded-lg"
              data-testid="mobile-header-cta-button"
            >
              {t("nav.cta")}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default Header;
